/**
 * Production Records page data layer (Plan 12).
 *
 * Reads:
 *   - `useMySchoolLocationsQuery()` — distinct admin schools/campuses
 *   - `useProductionRecordDaysQuery({ schoolLocationId, from, to })` — the
 *     page's per-day task rollup (Transport logs / Service records, PROD-1277)
 *   - `useCampusSummaryQuery()` — pending past-day count per campus (the sidebar badge)
 *   - `useProductionRecordsForSchoolQuery({ ...filters })` — single-campus list
 *     (the school print packet)
 *   - `useProductionRecordDetailQuery(id)` — record detail + print view
 *
 * Filter helpers (`rangeForWindow`, `filterDays`, ...) are pure and tested in
 * `client/test/api/production-records.test.ts`.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PRStatus = 'DRAFT' | 'AUTOMATED' | 'REVIEWED' | 'SUBMITTED' | 'AMENDED'
export type PRFormat = 'FULL' | 'PARTIAL'
import { type Mealtime, MEALTIME_LABELS, MEALTIMES } from '@/lib/mealtime'
export type { Mealtime }
export type TempCheckpoint = 'FACILITY' | 'DELIVERY' | 'SERVICE'
export type TempLogStatus = 'WITHIN_RANGE' | 'OUT_OF_RANGE' | 'NOT_RECORDED'
export type ProductionLineStatus = 'PENDING' | 'CONFIRMED' | 'PREPARED' | 'PACKED' | 'RECEIVED' | 'SERVED' | 'VOIDED'

export interface ProductionRecordRow {
  id: string
  serviceDate: string
  mealtime: Mealtime
  status: PRStatus
  format: PRFormat
  automatedSubmission: boolean
  submittedAt: string | null
  submittedByUserId: string | null
  signedByName: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
  vendorLocation: {
    id: string
    name: string
  }
  submittedBy: {
    id: string
    firstName: string
    lastName: string
    preferredName: string | null
  } | null
}

export interface ProductionRecordsForSchoolResponse {
  productionRecordsForSchool: ProductionRecordRow[]
}

export interface ProductionRecordDetail extends ProductionRecordRow {
  schoolLocation: {
    id: string
    name: string
  }
  lines: Array<{
    id: string
    menuItemId: string | null
    bundleId: string | null
    bundleDeliverableId: string | null
    parentLineId: string | null
    status: ProductionLineStatus
    /** Resolved meal/item name (bundle → deliverable → menu item), null if unresolvable. */
    name: string | null
    /** Cafeteria-entered buckets rolled up across all groups/grade tiers. */
    servedQty: number
    reimbursableQty: number
    nonReimbursableQty: number
    leftoverQty: number
  }>
  substitutions: Array<{
    id: string
    reason: string | null
    occurredAt: string
    originalIngredient: { id: string; name: string }
    substituteIngredient: { id: string; name: string }
  }>
  temperatureLogs: Array<{
    id: string
    checkpoint: TempCheckpoint
    temperatureValue: string
    temperatureUnit: string
    status: TempLogStatus
    reportedByUserId: string
    /** The meal (Bundle) + deliverable (package) this reading was logged against. */
    mealId: string | null
    mealName: string | null
    deliverableId: string | null
    deliverableName: string | null
  }>
  amendments: Array<{
    id: string
    amendedByUserId: string
    amendedAt: string
    reason: string
    changeSet: unknown
    amendedBy: {
      id: string
      firstName: string
      lastName: string
      preferredName: string | null
    } | null
  }>
  /** Footer write-ins: re-served leftovers, backup meals, dropped meals. */
  writeIns: Array<{
    id: string
    kind: 'LEFTOVER' | 'BACKUP' | 'DROPPED'
    menuItemId: string | null
    menuItemName: string | null
    description: string | null
    quantity: number
    reimbursableQty: number
    nonReimbursableQty: number
  }>
}

export interface ProductionRecordDetailResponse {
  productionRecord: ProductionRecordDetail | null
}

export interface MySchoolLocation {
  id: string
  name: string
  timeZone: string
}

export interface MySchoolLocationsResponse {
  /** Same projection consumers used previously — keeps page diff small. */
  me: {
    id: string
    memberships: Array<{
      id: string
      isArchived: boolean
      profileCode: string
      locationId: string | null
      location: { id: string; name: string; timeZone: string; type: string } | null
    }>
  } | null
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchMySchoolLocations(signal?: AbortSignal): Promise<{ schools: MySchoolLocation[] }> {
  return apiFetch<{ schools: MySchoolLocation[] }>('/api/production-records/my-schools', { signal })
}

export interface ProductionRecordsFilterVars {
  schoolLocationId: string
  fromDate?: string | null
  toDate?: string | null
  statuses?: PRStatus[]
  mealtimes?: Mealtime[]
  limit?: number
}

export async function fetchProductionRecordsForSchool(
  vars: ProductionRecordsFilterVars,
  signal?: AbortSignal,
): Promise<ProductionRecordsForSchoolResponse> {
  const { records } = await apiFetch<{ records: ProductionRecordRow[] }>('/api/production-records', {
    query: {
      schoolLocationId: vars.schoolLocationId,
      fromDate: vars.fromDate ?? undefined,
      toDate: vars.toDate ?? undefined,
      statuses: vars.statuses && vars.statuses.length > 0 ? vars.statuses.join(',') : undefined,
      mealtimes: vars.mealtimes && vars.mealtimes.length > 0 ? vars.mealtimes.join(',') : undefined,
      limit: vars.limit ?? 100,
    },
    signal,
  })
  return { productionRecordsForSchool: records }
}

export async function fetchProductionRecordDetail(
  id: string,
  signal?: AbortSignal,
): Promise<ProductionRecordDetailResponse> {
  const { record } = await apiFetch<{ record: ProductionRecordDetail | null }>(
    `/api/production-records/${encodeURIComponent(id)}`,
    { signal },
  )
  return { productionRecord: record }
}

// ---------------------------------------------------------------------------
// Two-part compliance document (GET /:id/document) — wire mirror of
// apps/ordo/server/src/managers/production-record-document.ts
// ---------------------------------------------------------------------------

export interface DocumentAddress {
  line1: string
  line2: string | null
  city: string
  stateProvince: string
  postalCode: string
}

export interface DocumentPartyBlock {
  id: string
  name: string
  address: DocumentAddress | null
}

export interface DocumentSignatureBlock {
  name: string | null
  signedAt: string | null
  source: 'PRODUCTION_RECORD' | 'SHIPMENT_EVENT' | null
}

export interface DocumentCreditingEntry {
  groupKind: string
  label: string
  display: string
}

export interface DocumentCounts {
  ordered: number | null
  /** Ordo-provisioned prepared count (the materialized shipment plan). */
  prepared: number | null
  delivered: number | null
  /** Null on rows that carry no service counts (grafted component leaves). */
  served: number | null
  reimbursable: number | null
  nonReimbursable: number | null
  leftover: number | null
  /**
   * PROD-1279: which of `ordered` / `prepared` / `delivered` the SCHOOL
   * entered on its own record (vs. the shipment-derived default). The
   * service seeds always emit it; optional here so the literal fixtures in
   * `client/test` keep compiling. `record-insights.ts` lets an entered
   * figure win over the transport walk's.
   */
  entered?: { ordered: boolean; prepared: boolean; delivered: boolean }
}

export interface DocumentPortionRow {
  gradeRangeName: string | null
  /** Collapsed grade span for the tier ("PK-8"); falls back to the name. */
  gradeSpan: string | null
  /**
   * PROD-1279: the school's OWN per-tier figures, present only when the
   * school entered them on the record's roster bucket (the server emits
   * null otherwise, never a shipment-derived proxy). Optional on the client
   * so existing fixtures keep compiling; the server type requires them.
   */
  ordered?: number | null
  prepared?: number | null
  delivered?: number | null
  served: number
  reimbursable: number
  nonReimbursable: number
  leftover: number
}

export interface DocumentLineNode {
  kind: 'BUNDLE' | 'DELIVERABLE' | 'COMPONENT'
  bundleId: string | null
  bundleDeliverableId: string | null
  componentId: string | null
  menuItemId: string | null
  recipeId: string | null
  /**
   * PROD-1441: the recipe's human code (`RCP-000206`), the identifier a
   * reviewer looks up; the cuid above never prints. Optional on the client so
   * the literal fixtures keep compiling; the server always sets it (null when
   * the line has no recipe).
   */
  recipeCode?: string | null
  /** PROD-1441: the raw `Component.kind` of a component row, for the sections (main / sides / condiments). */
  componentKind?: string | null
  /**
   * PROD-1441: a SEALED KIT — a deliverable packing two or more components.
   * The row carries every count and reading; `children` holds its count-less
   * component sub-rows. The one case where `children` is non-empty.
   */
  isKit?: boolean
  /** PROD-1441: the grade span the item's MENU was planned for (`K-8`), for the grade-group header. */
  menuGradeSpan?: string | null
  /** PROD-1441: the paper's Crediting source (`Std. recipe`, `CN label`, …); null when nothing on file says. */
  creditingSource?: string | null
  name: string
  /** Component-kind eyebrow ("MAIN", "MILK/BEVERAGE"); null = no eyebrow. */
  kindLabel: string | null
  servingLabel: string | null
  gradeRangeName: string | null
  /** Collapsed grade span the line covers ("PK-8", "9-12"). */
  gradeSpan: string | null
  /**
   * PROD-1279: where `gradeSpan` came from. `SHIPMENT` = the shipment line's
   * own grade range, `ROSTER` = the service line's roster tiers, `MENU` = the
   * item's menu grade tags (a whole-campus line naming the band it was SOLD
   * to). Only a shipped or served band may mint a per-band row; a MENU span
   * is legend text. Optional on the client so the literal fixtures keep
   * compiling (an absent field is the pre-PROD-1279 wire, where every span
   * was a real band); the server always sets it, null iff `gradeSpan` is.
   */
  gradeSpanSource?: 'SHIPMENT' | 'ROSTER' | 'MENU' | null
  recipeServingSize: string | null
  portions: DocumentPortionRow[]
  isBulk: boolean
  /** Packaging/utensil plan lines — excluded from item-level rollups. */
  isPackaging: boolean
  crediting: DocumentCreditingEntry[] | null
  creditingMissing: boolean
  deliveryRateBps: number | null
  /**
   * Per-line OVS/SS from the line's own menu (service log bundles and
   * deliverables). Rendered ONLY when the document-level `serviceStyle`
   * is null — a mixed-menu day one header field cannot represent.
   */
  serviceStyle: 'OVS' | 'SS' | null
  counts: DocumentCounts
  /** Always `[]` — both logs are flat row lists; kept for wire compatibility. */
  children: DocumentLineNode[]
}

/** The HACCP band a reading is judged against (the item's temperature class). */
export type TempBand = 'HOT' | 'CHILLED' | 'AMBIENT'

export interface DocumentTempReading {
  checkpoint: 'FACILITY' | 'DELIVERY' | 'SERVICE'
  lineName: string | null
  /** Item identity (matches line-node ids); all null on whole-shipment/-record readings. */
  bundleDeliverableId: string | null
  componentId: string | null
  menuItemId: string | null
  value: string
  unit: string
  status: 'WITHIN_RANGE' | 'OUT_OF_RANGE' | 'NOT_RECORDED'
  /** Null when the item's required band is unknowable (whole-shipment/legacy rows). */
  band: TempBand | null
  loggedAt: string | null
  /**
   * PROD-1279: where the reading was written. `SHIPMENT_EVENT` rows come off
   * a receipt / facility check on the shipment; `PRODUCTION_RECORD` rows are
   * record-scoped entries the school typed on the production record itself.
   * Optional on the client (fixtures); the server always sets it.
   */
  source?: 'SHIPMENT_EVENT' | 'PRODUCTION_RECORD'
}

export interface DocumentTransportGroup {
  deliveryMode: string
  handoutMode: 'INDIVIDUAL' | 'BULK_TRAY'
  deliveryDate: string
  deliveryDateDiffersFromService: boolean
  shipmentIds: string[]
  receivedAt: string | null
  temps: DocumentTempReading[]
  kitchenSignature: DocumentSignatureBlock
  deliverySignature: DocumentSignatureBlock
  lines: DocumentLineNode[]
}

export interface DocumentServiceGroup {
  deliveryMode: string | null
  handoutMode: 'INDIVIDUAL' | 'BULK_TRAY' | null
  temps: DocumentTempReading[]
  lines: DocumentLineNode[]
}

export type ProductionWriteInKind = 'LEFTOVER' | 'BACKUP' | 'DROPPED'

/** Footer write-in: a meal served or lost outside the shipment-derived lines. */
export interface DocumentWriteIn {
  id: string
  kind: ProductionWriteInKind
  /** Display name: the menu item for LEFTOVER, else the free-text description. */
  name: string
  description: string | null
  quantity: number
  /** Counts toward the claim; always 0 for DROPPED. */
  reimbursable: number
  nonReimbursable: number
}

export interface DocumentSpecialDietMeal {
  orderItemId: string
  accommodationId: string
  studentName: string
  mealName: string
  ordered: number
  served: number
  reimbursable: number
  documentedException: boolean
}
export interface ProductionRecordDocument {
  /** Supporting detail only; already represented in ordinary service counts. */
  specialDietMeals?: DocumentSpecialDietMeal[]
  id: string
  status: string
  format: string
  serviceDate: string
  mealtime: string
  serviceStyle: 'OVS' | 'SS' | null
  serviceStyleSource: string | null
  /** When the record was first materialized (the "record exists" timeline step). */
  generatedAt: string | null
  organization: { id: string; name: string }
  school: DocumentPartyBlock
  kitchen: DocumentPartyBlock
  transport: { groups: DocumentTransportGroup[] }
  service: { groups: DocumentServiceGroup[]; signature: DocumentSignatureBlock }
  /** Footer section: write-ins + dropped meals outside the shipment lines. */
  writeIns: DocumentWriteIn[]
  substitutions: Array<{
    id: string
    reason: string | null
    occurredAt: string
    originalIngredientName: string | null
    substituteIngredientName: string | null
  }>
  amendments: Array<{
    id: string
    amendedAt: string
    reason: string
    amendedByName: string | null
    /** Raw append-only change payload (shape varies by write path). */
    changeSet: unknown
  }>
  notes: string | null
}

export async function fetchProductionRecordDocument(
  id: string,
  signal?: AbortSignal,
): Promise<ProductionRecordDocument> {
  const { document } = await apiFetch<{ document: ProductionRecordDocument }>(
    `/api/production-records/${encodeURIComponent(id)}/document`,
    { signal },
  )
  return document
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useMySchoolLocationsQuery() {
  return useQuery({
    queryKey: ['production-records', 'my-schools'],
    queryFn: ({ signal }) => fetchMySchoolLocations(signal),
  })
}

export function useProductionRecordsForSchoolQuery(vars: ProductionRecordsFilterVars) {
  return useQuery<ProductionRecordsForSchoolResponse>({
    queryKey: ['production-records', 'list', vars],
    queryFn: ({ signal }) => fetchProductionRecordsForSchool(vars, signal),
    enabled: Boolean(vars.schoolLocationId),
  })
}

// ---------------------------------------------------------------------------
// Per-day task rollup (PROD-1277) — the Production Records page's model
// ---------------------------------------------------------------------------

export type ProductionRecordTaskStatus = 'NONE' | 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETE'

export interface ProductionRecordDayRecordWire {
  id: string
  mealtime: Mealtime
  vendorName: string | null
  status: PRStatus
  format: PRFormat
  submittedAt: string | null
  submittedByName: string | null
  signedByName: string | null
}

export interface ProductionRecordDayWire {
  /** yyyy-mm-dd */
  serviceDate: string
  /** Distinct mealtimes on the day, time-of-day order. */
  mealtimes: Mealtime[]
  transport: {
    total: number
    received: number
    /** DELIVERY-axis dates feeding this service day, ascending. */
    deliveryDates: string[]
    /** The subset still to receive — the Transport logs wizard runs one step per date. */
    unreceivedDeliveryDates: string[]
    status: ProductionRecordTaskStatus
  }
  service: {
    records: ProductionRecordDayRecordWire[]
    /** Shipments exist but no record was generated yet — the card renders disabled. */
    recordsMissing: boolean
    status: ProductionRecordTaskStatus
  }
  complete: boolean
}

export interface ProductionRecordDaysResponse {
  days: ProductionRecordDayWire[]
}

/** A type alias (not an interface) so it satisfies `apiFetch`'s query record. */
export type ProductionRecordDaysVars = {
  schoolLocationId: string
  /** ISO datetimes (inclusive), as produced by `rangeForWindow`. */
  from: string
  to: string
}

export async function fetchProductionRecordDays(
  vars: ProductionRecordDaysVars,
  signal?: AbortSignal,
): Promise<ProductionRecordDaysResponse> {
  return apiFetch<ProductionRecordDaysResponse>('/api/production-records/days', { query: vars, signal })
}

export const productionRecordDaysKey = (vars: ProductionRecordDaysVars) => ['production-records', 'days', vars] as const

export function useProductionRecordDaysQuery(vars: ProductionRecordDaysVars | null) {
  return useQuery<ProductionRecordDaysResponse>({
    enabled: Boolean(vars),
    queryKey: productionRecordDaysKey(vars ?? { schoolLocationId: '', from: '', to: '' }),
    queryFn: ({ signal }) => fetchProductionRecordDays(vars as ProductionRecordDaysVars, signal),
    // Switching campus tab or window re-keys the query; keep the previous
    // list on screen (dimmed by the page) instead of flashing the skeleton.
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  })
}

export interface CampusSummaryResponse {
  campuses: Array<{ locationId: string; pendingDays: number }>
}

export async function fetchCampusSummary(signal?: AbortSignal): Promise<CampusSummaryResponse> {
  return apiFetch<CampusSummaryResponse>('/api/production-records/campus-summary', { signal })
}

/**
 * The sidebar's Production Records badge: every campus-day the user may see
 * that still carries an open Transport logs or Service records task, summed
 * over the campuses (each campus-day is one record to complete). Undefined
 * (not loaded) and an empty list both read 0.
 */
export function sumPendingDays(campuses: ReadonlyArray<CampusSummaryResponse['campuses'][number]> | undefined): number {
  return (campuses ?? []).reduce((sum, campus) => sum + Math.max(0, campus.pendingDays), 0)
}

/** `refetchInterval` lets the sidebar badge poll on the Tasks cadence; the page reads once per 30s stale window. */
export function useCampusSummaryQuery(enabled = true, options: { refetchInterval?: number } = {}) {
  return useQuery<CampusSummaryResponse>({
    enabled,
    queryKey: ['production-records', 'campus-summary'],
    queryFn: ({ signal }) => fetchCampusSummary(signal),
    staleTime: 30 * 1000,
    refetchInterval: options.refetchInterval,
  })
}

export function useProductionRecordDocumentQuery(
  id: string | null,
  options: {
    /**
     * Keep the previous record on screen while the next one loads — the
     * record page swaps ids when a mealtime tab is picked, and a record is
     * the same shape as the record before it, so it must not flash a
     * skeleton between them.
     */
    keepPreviousData?: boolean
  } = {},
) {
  return useQuery<ProductionRecordDocument>({
    queryKey: ['production-records', 'document', id],
    queryFn: ({ signal }) => fetchProductionRecordDocument(id ?? '', signal),
    enabled: id !== null && id !== '',
    staleTime: 30 * 1000,
    placeholderData: options.keepPreviousData ? keepPreviousData : undefined,
  })
}

export function useProductionRecordDetailQuery(id: string | null) {
  return useQuery<ProductionRecordDetailResponse>({
    queryKey: ['production-records', 'detail', id],
    queryFn: ({ signal }) => fetchProductionRecordDetail(id ?? '', signal),
    enabled: id !== null && id !== '',
  })
}

// ---------------------------------------------------------------------------
// Filter helpers (pure — covered by unit tests)
// ---------------------------------------------------------------------------

/**
 * The four windows the Production Records page offers. `thisWeek` is the
 * default and deliberately runs Mon→Sun of the CURRENT week, so the days
 * still to be cooked show alongside the ones already served (records
 * materialize as soon as orders are submitted). The other three look
 * backward for review.
 */
export type DateWindow = 'thisWeek' | 'lastWeek' | 'last30' | 'last60'

/**
 * Day-level status filter (PROD-1277). A day is `pending` while either of
 * its tasks (Transport logs / Service records) is open and `complete` once
 * both are done — record-level statuses (Draft / Submitted / ...) are no
 * longer a page filter; they show on the record detail.
 */
export type DayStatusFilter = 'pending' | 'complete'

/**
 * What the status control can be set to. SINGLE-VALUED on purpose: the two
 * states are mutually exclusive and exhaustive, so a multi-select could
 * express only three things — pending, complete, or BOTH, and "both" is
 * indistinguishable from "all". Picking Complete after Pending therefore
 * looked like the filter had stopped working, because the days it had just
 * hidden came straight back. A single choice with an explicit `all` says the
 * same three things without the dead state.
 */
export type DayStatusChoice = DayStatusFilter | 'all'

export interface FilterState {
  dateWindow: DateWindow
  status: DayStatusChoice
  mealtimes: ReadonlySet<Mealtime>
  /**
   * Multi-campus schools only (PROD-1279 part two): the campuses whose
   * cards the timeline lists; an empty set means every campus. It took over
   * from the campus tabs when each campus became a card under the day.
   */
  campuses: ReadonlySet<string>
}

export const DATE_WINDOW_LABEL: Record<DateWindow, string> = {
  thisWeek: 'This week',
  lastWeek: 'Last week',
  last30: 'Past 30 days',
  last60: 'Past 60 days',
}

/** Every window, in menu order. The single source of truth for the picker. */
export const DATE_WINDOW_OPTIONS: ReadonlyArray<{ value: DateWindow; label: string }> = [
  { value: 'thisWeek', label: DATE_WINDOW_LABEL.thisWeek },
  { value: 'lastWeek', label: DATE_WINDOW_LABEL.lastWeek },
  { value: 'last30', label: DATE_WINDOW_LABEL.last30 },
  { value: 'last60', label: DATE_WINDOW_LABEL.last60 },
]

/** The day-status pill tabs on the Production Records toolbar, in order. */
export const DAY_STATUS_OPTIONS: ReadonlyArray<{ value: DayStatusChoice; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Incomplete' },
  { value: 'complete', label: 'Complete' },
]

/** Every mealtime the platform schedules, in time-of-day order (the canonical list). */
export const MEALTIME_OPTIONS: ReadonlyArray<{ value: Mealtime; label: string }> = MEALTIMES.map((value) => ({
  value,
  label: MEALTIME_LABELS[value],
}))

/** UTC Monday 00:00 of the ISO week containing `d` (Sunday belongs to the prior week). */
function mondayOf(d: Date): Date {
  const monday = new Date(d)
  const dow = monday.getUTCDay() // 0 Sun … 6 Sat
  monday.setUTCDate(monday.getUTCDate() + (dow === 0 ? -6 : 1 - dow))
  monday.setUTCHours(0, 0, 0, 0)
  return monday
}

export function rangeForWindow(window: DateWindow, now: Date = new Date()): { from: string; to: string } {
  const to = new Date(now)
  const from = new Date(now)
  switch (window) {
    // Mon→Sun of the current week: the upcoming days of this week are the
    // whole point (staff look ahead at what's coming), so `to` runs to the
    // end of the week rather than stopping at today.
    case 'thisWeek': {
      const monday = mondayOf(now)
      const sunday = new Date(monday)
      sunday.setUTCDate(sunday.getUTCDate() + 6)
      sunday.setUTCHours(23, 59, 59, 999)
      return { from: monday.toISOString(), to: sunday.toISOString() }
    }
    case 'lastWeek': {
      const monday = mondayOf(now)
      monday.setUTCDate(monday.getUTCDate() - 7)
      const sunday = new Date(monday)
      sunday.setUTCDate(sunday.getUTCDate() + 6)
      sunday.setUTCHours(23, 59, 59, 999)
      return { from: monday.toISOString(), to: sunday.toISOString() }
    }
    case 'last30':
      from.setUTCDate(from.getUTCDate() - 30)
      break
    case 'last60':
      from.setUTCDate(from.getUTCDate() - 60)
      break
  }
  return { from: from.toISOString(), to: to.toISOString() }
}

/**
 * The `/days` query variables for a filter window + campus.
 *
 * The upper bound is CLAMPED TO NOW. `thisWeek` runs Mon→Sun, so without the
 * clamp the page listed days that have not happened yet — rows whose tasks
 * nobody can fill, each carrying an "Opens Thu 09/10" chip. Production
 * records are filled after the fact; a future day is pure clutter here.
 */
export function daysVariablesForFilter(
  state: Pick<FilterState, 'dateWindow'>,
  schoolLocationId: string,
  now: Date = new Date(),
): ProductionRecordDaysVars {
  const { from, to } = rangeForWindow(state.dateWindow, now)
  const nowIso = now.toISOString()
  return { schoolLocationId, from, to: to > nowIso ? nowIso : to }
}

/** The presets from narrowest to widest — the order `windowForDay` searches. */
const DATE_WINDOWS_BY_SPAN: ReadonlyArray<DateWindow> = ['thisWeek', 'lastWeek', 'last30', 'last60']

/** Whether a yyyy-mm-dd calendar day falls inside a window preset (by UTC calendar day). */
export function windowContainsDay(window: DateWindow, dayKey: string, now: Date = new Date()): boolean {
  const { from, to } = rangeForWindow(window, now)
  return from.slice(0, 10) <= dayKey && dayKey <= to.slice(0, 10)
}

/**
 * The window to list a calendar day in: the current preset when it already
 * covers the day, else the narrowest preset that does; null when no preset
 * reaches it (older than 60 days). The calendar rail uses this to widen the
 * list when a clicked day is outside the loaded window.
 */
export function windowForDay(dayKey: string, current: DateWindow, now: Date = new Date()): DateWindow | null {
  if (windowContainsDay(current, dayKey, now)) {
    return current
  }
  return DATE_WINDOWS_BY_SPAN.find((window) => windowContainsDay(window, dayKey, now)) ?? null
}

/**
 * Client-side status + mealtime filtering over the day rows (the window is
 * the only server-side filter). Status is day-level and single-valued
 * (`all` / `pending` / `complete`); the mealtime filter keeps days that
 * schedule ANY selected mealtime — a pending day's tasks are per day, so a
 * Breakfast filter still shows the whole day's work — and narrows a complete
 * day's record cards to the selected mealtimes. An empty mealtime set means
 * "all".
 */
export function filterDays<
  T extends Pick<ProductionRecordDayWire, 'complete' | 'mealtimes'> & {
    service: Pick<ProductionRecordDayWire['service'], 'records'>
  },
>(days: readonly T[], state: Pick<FilterState, 'status' | 'mealtimes'>): T[] {
  return days
    .filter((day) => {
      if (state.status !== 'all' && state.status !== (day.complete ? 'complete' : 'pending')) {
        return false
      }
      if (state.mealtimes.size > 0 && !day.mealtimes.some((m) => state.mealtimes.has(m))) {
        return false
      }
      return true
    })
    .map((day) =>
      state.mealtimes.size > 0 && day.complete
        ? {
            ...day,
            service: {
              ...day.service,
              records: day.service.records.filter((r) => state.mealtimes.has(r.mealtime)),
            },
          }
        : day,
    )
}

export function defaultFilterState(): FilterState {
  return {
    // Past 30 days, not this week: records are filled after the fact, often
    // at the end of a week, so the backlog is what the page is FOR.
    dateWindow: 'last30',
    status: 'all',
    mealtimes: new Set<Mealtime>(),
    campuses: new Set<string>(),
  }
}

/**
 * Distinct school Locations the current user has SCHOOL_ADMIN
 * membership at — derived from the REST `my-schools` response which is
 * already filtered + flattened.
 */
export function distinctAdminSchools(data: { schools: MySchoolLocation[] } | null | undefined): MySchoolLocation[] {
  return data?.schools ?? []
}

/**
 * Sort temperature logs into the operational flow order (FACILITY ->
 * DELIVERY -> SERVICE) rather than alphabetic. Mirrors the server-side
 * helper of the same name.
 */
export function sortTemperatureLogsByFlow<T extends { checkpoint: string }>(rows: readonly T[]): T[] {
  const flowOrder = ['FACILITY', 'DELIVERY', 'SERVICE'] as const
  const indexOf = (c: string) => {
    const i = flowOrder.indexOf(c as (typeof flowOrder)[number])
    return i === -1 ? flowOrder.length : i
  }
  return [...rows].sort((a, b) => indexOf(a.checkpoint) - indexOf(b.checkpoint))
}

/** Minimal shape needed to group temp logs — the detail temp-log rows satisfy it. */
export interface TempLogGroupable {
  mealId: string | null
  mealName: string | null
  deliverableId: string | null
  deliverableName: string | null
  checkpoint: string
  temperatureValue: string
  temperatureUnit: string
  status: string
}

export interface GroupedTempMeal<T> {
  name: string
  deliverables: Array<{ name: string; logs: Array<T & { readingCount: number }> }>
}

/** Total rows across a grouped temp log (post-dedup) — drives section counts. */
export function countGroupedTemperatureLogs<T>(groups: ReadonlyArray<GroupedTempMeal<T>>): number {
  return groups.reduce(
    (sum, meal) => sum + meal.deliverables.reduce((inner, deliverable) => inner + deliverable.logs.length, 0),
    0,
  )
}

/**
 * Regroup a per-mealtime record's temperature readings back into their
 * source meals: meal (Bundle / component) → deliverable (package) →
 * checkpoints. Readings with no resolvable meal collapse under "Meal";
 * no resolvable deliverable collapses under "Temperature readings".
 * Meals + deliverables are sorted by name; reading order within a
 * deliverable is preserved (feed it flow-sorted input for HACCP order).
 * Shared by the detail drawer and the USDA print view.
 *
 * IDENTICAL readings within a deliverable collapse into one row
 * (`readingCount` carries the multiplicity). One physical measurement is
 * fanned out to every shipment line it covers — on group-split campuses
 * that's one line PER HOMEROOM/GROUP, which used to render as 5-16
 * indistinguishable repeats per item. Distinct values (a real re-check
 * or an out-of-range reading) still render as their own rows.
 */
export function groupTemperatureLogsByMeal<T extends TempLogGroupable>(logs: readonly T[]): Array<GroupedTempMeal<T>> {
  const meals = new Map<
    string,
    { name: string; deliverables: Map<string, { name: string; readings: Map<string, T & { readingCount: number }> }> }
  >()
  for (const log of logs) {
    const mealKey = log.mealId ?? '__meal'
    const meal = meals.get(mealKey) ?? { name: log.mealName ?? 'Meal', deliverables: new Map() }
    const dKey = log.deliverableId ?? '__deliverable'
    const deliverable = meal.deliverables.get(dKey) ?? {
      name: log.deliverableName ?? 'Temperature readings',
      readings: new Map<string, T & { readingCount: number }>(),
    }
    const readingKey = [log.checkpoint, log.temperatureValue, log.temperatureUnit, log.status].join('::')
    const existing = deliverable.readings.get(readingKey)
    if (existing) {
      existing.readingCount += 1
    } else {
      deliverable.readings.set(readingKey, { ...log, readingCount: 1 })
    }
    meal.deliverables.set(dKey, deliverable)
    meals.set(mealKey, meal)
  }
  return Array.from(meals.values())
    .map((m) => ({
      name: m.name,
      deliverables: Array.from(m.deliverables.values())
        .map((d) => ({ name: d.name, logs: Array.from(d.readings.values()) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * USDA HACCP bounds (informational only).
 */
export const USDA_HACCP_HOT_HOLD_MIN_F = 135
export const USDA_HACCP_COLD_HOLD_MAX_F = 41
